import React, { useReducer, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'

import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { DateTimePicker, Select, Text, Checkbox } from 'components/forms'
import { CashRegisters, DatePeriod, filterReducer } from '../filterFragments'
import { CardsRow, CardCol } from '../filterFragments/styled'
import listCardsGroups from 'sections/loyaltyCard/gql/listLoyaltyCardGroups'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { listCashRegisters } from 'gql/query'
import { useQuery } from 'components/graphql'
import ProfileSettings from 'components/ProfileSettings/ProfileSettings'

const Filter = ({ onFilter, showUsersCol, setShowUsersCol }) => {
	const { data } = useQuery(listCashRegisters, { fetchPolicy: 'cache-first' })
	const defaultFilter = {
		from: moment().utc().startOf('day').toISOString(),
		to: moment().utc().endOf('day').toISOString(),
		timeStart: moment().utc().startOf('day').format('HH:mm:ss.SSSZ'),
		timeEnd: moment().utc().endOf('day').format('HH:mm:ss.SSSZ'),
		type: 2,
		showReset: false,
		showCanceled: false,
		cashRegisterIds: data?.listCashRegisters?.map(({ id }) => id) || [],
		cardNumber: null,
		active: 0,
		transactionStatsType: null,
		transactionSource: null
	}
	const [variables, dispatchVariables] = useReducer(filterReducer, defaultFilter)
	const { from, to } = variables
	const [t] = useTranslation()

	const handleFilter = () => {
		const { timeStart, timeEnd, ...restParams } = variables
		const [hoursFrom, minsFrom] = (timeStart || '00:00').split(':')
		const [hoursTo, minsTo] = (timeEnd || '00:00').split(':')
		const newFrom = new Date(from)
		const newTo = new Date(to)
		newFrom.setUTCHours(hoursFrom)
		newFrom.setUTCMinutes(minsFrom)
		newTo.setUTCHours(hoursTo)
		newTo.setUTCMinutes(minsTo)
		onFilter({
			...restParams,
			from: newFrom.toISOString(),
			to: newTo.toISOString()
		})
	}

	const discountGroupOptions = useMemo(() => {
		const out = []
		for(let i = 1; i <= 10; i++) {
			out.push({ value: i, label: i })
		}
		return out
	}, [])

	const handleSetProfileSettings = settings => {
		const filterToSet = settings ? settings : defaultFilter
		dispatchVariables({
			type: 'hydrateFromSaved',
			value: filterToSet
		})
	}

	return(
		<>
			<Row>
				<Col xs={12} style={{ marginLeft: -20 }}>
					<ProfileSettings filters={variables} onUpdateFilters={handleSetProfileSettings} nameLocalStorage={'loyaltyCardsStatisticsProfiles'} />
				</Col>
			</Row>

			<Row>
				<Col xs>
					<DatePeriod
						value={{ fromDateTime: from, toDateTime: to }}
						onChange={value => dispatchVariables({ value, type: 'dateTime' })}
						label={t('common.strings.area_from')}
						labelPeriod={{
							from: t('common.strings.area_from'),
							to: t('common.strings.area_to')
						}}
					/>
					<DateTimePicker
						noForm
						value={{ from: variables.timeStart, to: variables.timeEnd }}
						onChange={value => dispatchVariables({ value, type: 'timePeriod' })}
						picker='time'
						addDateToTime={false}
						interval
						label={{ from: t('common.strings.from'), to: t('common.strings.to') }}
					/>
				</Col>
				<Col xs>
					<Select
						noForm
						value={variables.type}
						onChange={val => dispatchVariables({ value: val?.value, type: 'type' })}
						options={[
							{ value: 1, label: t('statistic_section.strings.rfid_in_cafe') },
							{ value: 2, label: t('statistic_section.strings.rfid_loyalty') },
							{ value: 3, label: t('statistic_section.strings.rfid_debit') }
						]}
						label={t('loyalty_card_section.fields.type')}
					/>
					<Select
						noForm
						value={variables?.active}
						onChange={val => dispatchVariables({ value: val?.value, type: 'active' })}
						options={[
							{ value: 0, label: t('statistic_section.strings.all_cards') },
							{ value: 1, label: t('statistic_section.strings.active_cards') },
							{ value: 2, label: t('statistic_section.strings.non_active_cards') }
						]}
						label={t('statistic_section.strings.active')}
					/>
					<Select
						noForm
						value={variables?.transactionStatsType}
						onChange={val => dispatchVariables({ value: val?.value, type: 'transactionStatsType' })}
						isClearable
						options={[
							{ value: 'purchase', label: t('statistic_section.strings.purchase') },
							{ value: 'refil', label: t('loyalty_card_section.strings.credit') }
						]}
						label={t('statistic_section.strings.loyalty_transaction_stats_type')}
					/>
				</Col>
				<Col xs>
					<Select
						noForm
						value={variables?.groupId}
						onChange={val => dispatchVariables({ value: Number(val?.id), type: 'groupId' })}
						remoteOptions={{
							query: listCardsGroups
						}}
						label={t('statistic_section.strings.group')}
						valueField='id'
						labelField='name'
						isClearable
					/>
					<Select
						noForm
						value={variables?.discountGroupId}
						onChange={val => dispatchVariables({ value: val?.value, type: 'discountGroupId' })}
						options={discountGroupOptions}
						label={t('statistic_section.strings.discount_num')}
						isClearable
					/>
					<Select
						noForm
						value={variables?.transactionSource}
						onChange={val => dispatchVariables({ value: val?.value, type: 'transactionSource' })}
						isClearable
						options={[
							{ value: 'WEB', label: t('statistic_section.strings.loyalty_source_web') },
							{ value: 'NON_WEB', label: t('statistic_section.strings.loyalty_source_nonweb') }
						]}
						label={t('statistic_section.strings.loyalty_transaction_source')}
					/>
				</Col>
				<Col xs>
					<Row>
						<Col xs>
							<Text
								noForm
								value={variables.cardNumber || ''}
								onChange={e => dispatchVariables({ value: e?.target?.value, type: 'cardNumber' })}
								label={t('loyalty_card_section.fields.card_number')}
								shrink
							/>
						</Col>
						<Col xs>
							<Checkbox
								noForm
								checked={showUsersCol}
								onChange={val => setShowUsersCol(val?.target?.checked)}
								label={t('statistic_section.strings.show_users')}
							/>
							<Checkbox
								noForm
								checked={showUsersCol}
								onChange={val => dispatchVariables({ value: val?.target?.checked, type: 'showReset' })}
								label={t('statistic_section.strings.show_reset')}
							/>
							<Checkbox
								noForm
								checked={showUsersCol}
								onChange={val => dispatchVariables({ value: val?.target?.checked, type: 'showCanceled' })}
								label={t('statistic_section.strings.show_canceled')}
							/>
						</Col>
					</Row>
				</Col>
			</Row>
			<Divider />
			<ExpandableBlocks nested>
				<ContentBlock label={t('statistic_section.strings.filter')}>
					<CardsRow>
						<CardCol>
							<CashRegisters
								value={variables.cashRegisterIds || []}
								onChange={value => dispatchVariables({ value, type: 'cashRegisterIds' })}
							/>
						</CardCol>
					</CardsRow>
				</ContentBlock>
			</ExpandableBlocks>
			<Divider />
			<Button text={t('common.buttons.calculate')} onClick={handleFilter} />
		</>
	)
}

export default Filter
